import { ApiErrorResponse } from '../models/Api';
import { ApiError } from '../services/api';

const RECAPTCHA_SITE_KEY = '6Ld-VfsrAAAAAEArCPGFRIriiRAMZCloQ_SCRl9d';
const MAX_EXECUTE_ATTEMPTS = 3;
const EXECUTE_RETRY_DELAY_MS = 300;

// This is the expected message from the API when verification fails
const RECAPTCHA_VERIFICATION_FAILED_MESSAGE = 'reCAPTCHA verification failed';

// Message to user when verification fails after retries
export const RECAPTCHA_USER_ERROR_MESSAGE =
  'User verification failed. Please refresh the page and try again.';

type GrecaptchaApi = {
  ready: (callback: () => void) => void
  execute: (siteKey: string, options: { action: string }) => Promise<string>
}

function getGrecaptcha(): GrecaptchaApi | null {
  const grecaptcha = (window as any).grecaptcha as GrecaptchaApi | undefined;
  if (!grecaptcha || !grecaptcha.ready || !grecaptcha.execute) {
    return null;
  }

  return grecaptcha;
}

function delayAttempt(milliseconds: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, milliseconds));
}

function executeOnce(grecaptcha: GrecaptchaApi, action: string): Promise<string> {
  return new Promise((resolve, reject) => {
    grecaptcha.ready(() => {
      grecaptcha.execute(RECAPTCHA_SITE_KEY, { action })
        .then((token: string) => {
          if (!token) {
            reject(new Error(RECAPTCHA_USER_ERROR_MESSAGE));
            return;
          }
          resolve(token);
        })
        .catch(() => {
          reject(new Error(RECAPTCHA_USER_ERROR_MESSAGE));
        });
    });
  });
}

/**
 * Execute reCAPTCHA v3 and return a token for the supplied action
 *
 * Retries execute up to 3 times to try to handle browser/network failures.
 *
 * @throws Error when grecaptcha is unavailable or all execute attempts fail
 */
export async function executeRecaptcha(action: string): Promise<string> {
  const grecaptcha = getGrecaptcha();
  if (!grecaptcha) {
    throw new Error(RECAPTCHA_USER_ERROR_MESSAGE);
  }

  let lastError: Error = new Error(RECAPTCHA_USER_ERROR_MESSAGE);

  for (let attempt = 1; attempt <= MAX_EXECUTE_ATTEMPTS; attempt++) {
    try {
      return await executeOnce(grecaptcha, action);
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(RECAPTCHA_USER_ERROR_MESSAGE);
      if (attempt < MAX_EXECUTE_ATTEMPTS) {
        await delayAttempt(EXECUTE_RETRY_DELAY_MS);
      }
    }
  }

  throw lastError;
}

function getApiErrorMessage(error: unknown): string | null {
  if (error instanceof ApiErrorResponse) {
    return error.error.message;
  }

  if (error instanceof ApiError && error.data?.error?.message) {
    return error.data.error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return null;
}

/**
 * True when verification failed on the client or the API rejected the reCAPTCHA token
 */
export function isRecaptchaVerificationError(error: unknown): boolean {
  const message = getApiErrorMessage(error);
  return message != null && (
    message.indexOf(RECAPTCHA_VERIFICATION_FAILED_MESSAGE) !== -1
    || message === RECAPTCHA_USER_ERROR_MESSAGE
  );
}

/**
 * Run a payment POST with a reCAPTCHA token, retrying once with a fresh token if the API
 * rejects the first token as a reCAPTCHA verification failure.
 */
export async function postWithRecaptchaRetry<T>(
  action: string,
  post: (recaptchaToken: string) => Promise<T>
): Promise<T> {
  const postOnce = async (): Promise<T> => {
    const recaptchaToken = await executeRecaptcha(action);
    return post(recaptchaToken);
  };

  try {
    return await postOnce();
  } catch (error) {
    if (!isRecaptchaVerificationError(error)) {
      throw error;
    }

    try {
      return await postOnce();
    } catch (retryError) {
      if (isRecaptchaVerificationError(retryError)) {
        throw new Error(RECAPTCHA_USER_ERROR_MESSAGE);
      }
      throw retryError;
    }
  }
}
